export type DebouncedFn<OriginalFn extends (...args: any[]) => any> = ((
    ...args: Parameters<OriginalFn>
) => void) & {
    cancel: () => void;
    flush: () => ReturnType<OriginalFn>;
    isPending: () => boolean;
};

/** Takes in a function and executes it after a given `wait` time.
 * If the function is called again before the timer runs out, the timer is reset to the `wait` time.
 *
 * This is useful if you want to delay expensive sync functions (e.g. syncing inner state to a parent component) */
export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): DebouncedFn<T> {
    let lastCallArgs: Parameters<T> | undefined;
    let timerId: NodeJS.Timeout | undefined;

    function invoke(): ReturnType<T> {
        timerId = undefined;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return func(...lastCallArgs!);
    }

    function debounced(...args: Parameters<T>): void {
        lastCallArgs = args;

        if (timerId) clearTimeout(timerId);

        timerId = setTimeout(() => invoke(), wait);
    }
    debounced.cancel = function cancel() {
        if (timerId !== undefined) {
            clearTimeout(timerId);
        }
        lastCallArgs = undefined;
    };
    debounced.flush = function flush() {
        clearTimeout(timerId);
        return func(lastCallArgs);
    };
    debounced.isPending = function isPending() {
        return timerId !== undefined;
    };
    return debounced;
}
